import { MessageStatus } from '@/features/messages/models/message-status';
import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageStatusIconProps {
  status?: MessageStatus;
  isSending?: boolean;
  isOwn?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  isLast?: boolean;
}

/** Message status configuration for accessibility and display */
interface StatusConfig {
  label: string;
  icon: string;
  color?: 'primary' | 'inherit';
}

export const MessageStatusIcon = memo<MessageStatusIconProps>(({ status, isSending, isOwn, sx, className, isLast }) => {
  const { t } = useTranslation();

  if (!isOwn) return null;

  const statusConfig = useMemo((): StatusConfig | null => {
    if (isSending) {
      return { label: t('messages.status.sending'), icon: '', color: 'inherit' };
    }

    switch (status) {
      case MessageStatus.SENDING:
        return { label: t('messages.status.sending'), icon: '', color: 'inherit' };
      case MessageStatus.SENT:
        if (!isLast) return null;
        return { label: t('messages.status.sent'), icon: '✓', color: 'primary' };
      case MessageStatus.DELIVERED:
        return { label: t('messages.status.delivered'), icon: '', color: 'primary' };
      case MessageStatus.SEEN:
        return { label: t('messages.status.seen'), icon: '', color: 'inherit' };
      default:
        return { label: t('messages.status.sending'), icon: '', color: 'inherit' };
    }
  }, [isSending, status, isLast, t]);

  if (!statusConfig) return null;

  return (
    <Box
      component='span'
      role='status'
      aria-live='polite'
      aria-label={statusConfig.label}
      sx={{ display: 'inline-flex', justifyContent: 'end', ml: 0.5, ...sx }}
      className={className}
    >
      <Typography variant='caption' className='mex:!text-[10px]' fontWeight='bold' color={statusConfig.color}>
        {statusConfig.icon && `${statusConfig.icon} `}
        {statusConfig.label}
      </Typography>
    </Box>
  );
});

MessageStatusIcon.displayName = 'MessageStatusIcon';
